import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toPng } from "html-to-image";
import { Box, Button, CheckBox, Heading, Layer, Text } from "grommet";
import styled from "styled-components";

import DownloadIcon from "../icons/DownloadIcon";
import CloseIcon from "../icons/CloseIcon";
import { RootState } from "../store";
import RiskIcon from "./RiskIcon";
import { Screen, GeographyLevel, BaseMapLayer } from "../models";
import BoundaryLegend from "./BoundaryLegend";
import WRTCLogoPlain from "./WRCLogoPlain";
import { MapLegendComponent, MapLegendComponentProps } from "./MapLegend";
import VulnerablePopWidgetExpanded from "./VulnerablePopWidgetExpanded";
import MapboxLogoWhite from "./MapboxLogoBlack";
import ToggleButtonGroup from "./ToggleButtonGroup";
import { DOWNLOAD_MAX_PIXEL_RATIO, DOWNLOAD_MIN_PIXEL_RATIO, DOWNLOAD_TIMEOUT } from "../constants";

interface Props {
  readonly screen: Screen;
  readonly geographyLevel: GeographyLevel;
  readonly riskToHomesProps: MapLegendComponentProps;
  readonly likelihoodProps: MapLegendComponentProps;
  readonly rrzProps: MapLegendComponentProps;
  readonly vulPopBaseLayer: BaseMapLayer;
  readonly onClose: () => void;
}

const MapLegendContainer = styled(Box)`
  position: absolute;
  top: 0.2rem;
  left: 0.2rem;
  zindex: 10;
  borderradius: 0.2rem;
  boxshadow: 0 0 2px 0 rgba(0, 0, 0, 0.5);
  background-color: #fff;
  padding: 0.5rem;
`;

export const MapDownloadDialog = ({
  screen,
  geographyLevel,
  riskToHomesProps,
  likelihoodProps,
  rrzProps,
  vulPopBaseLayer,
  onClose
}: Props) => {
  const { header, riskLevel, description, definition, dataUrl } = useSelector(
    (state: RootState) => state.mapDownload
  );
  const [showSummaryStatistic, setShowSummaryStatistic] = useState(true);
  const [mapFormat, setMapFormat] = useState<string>("Wide");
  const [imageResolution, setImageResolution] = useState<string>("For digital use");
  const [isPreparing, setIsPreparing] = useState(false);

  const exportRef = useRef<HTMLDivElement>(null);

  const headerDisplay = useMemo(() => {
    if (screen !== Screen.VulnerablePopulations) return header;
    if (vulPopBaseLayer === BaseMapLayer.none) return header;
    if (vulPopBaseLayer === BaseMapLayer.wildfireLikelihood)
      return `${vulPopBaseLayer} and ${header}`;
    return `Wildfire ${vulPopBaseLayer} and ${header}`;
  }, [screen, header, vulPopBaseLayer]);

  const MapLegend = useCallback(() => {
    const showRiskToHomes =
      screen === Screen.RiskToHomes ||
      (screen === Screen.VulnerablePopulations && vulPopBaseLayer === BaseMapLayer.riskToHomes);
    const showWildfireLikelihood =
      screen === Screen.WildfireLikelihood ||
      (screen === Screen.VulnerablePopulations &&
        vulPopBaseLayer === BaseMapLayer.wildfireLikelihood);
    const showRRZ =
      screen === Screen.RiskReductionZones ||
      (screen === Screen.VulnerablePopulations &&
        vulPopBaseLayer === BaseMapLayer.riskReductionZones);

    if (showRiskToHomes) {
      return (
        <MapLegendContainer>
          <MapLegendComponent {...riskToHomesProps} smallLabel noPad />
        </MapLegendContainer>
      );
    }

    if (showWildfireLikelihood) {
      return (
        <MapLegendContainer>
          <MapLegendComponent {...likelihoodProps} smallLabel noPad />
        </MapLegendContainer>
      );
    }

    if (showRRZ) {
      return (
        <MapLegendContainer>
          <MapLegendComponent classLegend={rrzProps.classLegend} noLabel compact smallLabel />
        </MapLegendContainer>
      );
    }

    return null;
  }, [screen, likelihoodProps, riskToHomesProps, rrzProps, vulPopBaseLayer]);

  const onClickDownload = () => {
    const captureAndDownload = async () => {
      try {
        const element = exportRef.current;
        if (!element) return;

        const dataUrl = await toPng(element, {
          // Help with caching issues
          cacheBust: true,
          // Use 'pixelRatio' for resolution
          pixelRatio:
            imageResolution === "For digital use"
              ? DOWNLOAD_MIN_PIXEL_RATIO
              : DOWNLOAD_MAX_PIXEL_RATIO
        });

        const link = document.createElement("a");
        link.download = `${header.replaceAll(" ", "_")}.png`;
        link.href = dataUrl;
        link.click();
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setIsPreparing(false);
        onClose();
      }
    };
    setIsPreparing(true);
    setTimeout(captureAndDownload, DOWNLOAD_TIMEOUT);
  };

  return (
    <Layer modal animation="fadeIn" onEsc={onClose} onClickOutside={onClose}>
      <Box direction="row">
        {/* Map Config */}
        <Box
          pad={{ top: "none", horizontal: "medium", bottom: "medium" }}
          width="30rem"
          gap="medium"
          className="content-box"
          align="start"
        >
          <Heading level={3} margin="none" style={{ width: "100%" }}>
            Download map
          </Heading>
          <Box flex="grow" gap="medium" width="100%">
            <Box gap="small">
              <Text weight={700} color="#000">
                Details
              </Text>
              <CheckBox
                label={
                  <Text size="1.5rem" color="#000">
                    Summary statistic
                  </Text>
                }
                checked={showSummaryStatistic}
                onChange={event => setShowSummaryStatistic(event.target.checked)}
              />
            </Box>
            <Box gap="small">
              <Text weight={700} color="#000">
                Map format
              </Text>
              <ToggleButtonGroup
                options={["Wide", "Sqaure"]}
                setValue={setMapFormat}
                value={mapFormat}
              />
            </Box>
            <Box gap="small">
              <Text weight="bold" color="#000">
                Image resolution
              </Text>
              <ToggleButtonGroup
                options={["For digital use", "For print"]}
                setValue={setImageResolution}
                value={imageResolution}
              />
            </Box>
          </Box>

          <Box flex={false} width="100%">
            <Button
              color="highlight-800"
              size="medium"
              onClick={onClickDownload}
              messages={{
                busy: "Preparing chart",
                success: "Chart ready"
              }}
              busy={isPreparing}
              label="Download"
              aria-label="Download chart of exposed buildings by zone."
              icon={<DownloadIcon size="14px" color="var(--highlight-700)" aria-hidden />}
              style={{
                width: "100%",
                background: "var(--highlight-200)",
                justifyContent: isPreparing ? "center" : undefined
              }}
            />
          </Box>
        </Box>

        {/* Map Preview */}
        <Box
          pad={{ top: "none", horizontal: "medium", bottom: "medium" }}
          background="#625A55"
          width="64rem"
          height="82rem"
          gap="small"
          align="center"
        >
          {/* Close button */}
          <Box
            direction="row"
            height="5rem"
            pad={{ horizontal: "none", vertical: "1.2rem" }}
            justify="end"
            align="start"
            alignSelf="stretch"
          >
            <Button
              color="highlight-800"
              style={{
                background: "var(--warm-gray-200)",
                width: "2.5rem",
                height: "2.5rem",
                color: "var(--text-800)",
                padding: ".5rem .9rem",
                alignItems: "center",
                justifyContent: "center"
              }}
              size="medium"
              aria-label="Close map download dialog"
              icon={<CloseIcon size="12px" />}
              onClick={onClose}
            />
          </Box>

          {/* Preview */}
          <Box
            direction="column"
            margin={{ horizontal: "none", vertical: "auto" }}
            justify="center"
            align="center"
            gap="small"
          >
            <Box
              width={mapFormat === "Sqaure" ? "32.2rem" : "60rem"}
              border
              background="#fff"
              pad="small"
              gap="small"
              ref={exportRef}
            >
              {/* Heading */}
              <Box
                pad={{ bottom: "small" }}
                style={{ borderBottom: ".1rem solid var(--warm-gray-300)" }}
              >
                <Heading color="#000" level={4}>
                  {headerDisplay}
                </Heading>
              </Box>

              {/* Stats */}
              {showSummaryStatistic && (
                <Box background="white" direction="row" align="start" gap="small">
                  {riskLevel && <RiskIcon riskLevel={riskLevel} size={40} />}
                  <Box gap=".4rem">
                    <Text color="#000" size="1.2rem" weight={700}>
                      {description}
                    </Text>
                    <Text size="1.1rem">{definition}</Text>
                  </Box>
                </Box>
              )}

              {screen === Screen.VulnerablePopulations && (
                <VulnerablePopWidgetExpanded showOther={false} />
              )}

              {/* Map */}
              <Box height="30rem" overflow="hidden" style={{ position: "relative" }}>
                <img
                  src={dataUrl}
                  alt="Map to download"
                  height="100%"
                  width="100%"
                  style={{ objectFit: "cover" }}
                />

                {/* Legend */}
                <MapLegend />

                {/* WRC Logo */}
                <Box
                  height="3.7rem"
                  style={{
                    position: "absolute",
                    top: ".2rem",
                    right: ".2rem",
                    zIndex: 10,
                    opacity: 0.8,
                    borderRadius: ".2rem"
                  }}
                  pad=".5rem"
                  background="#fff"
                >
                  <WRTCLogoPlain height="27px" />
                </Box>

                {/* Mapbox Logo */}
                <Box
                  justify="end"
                  height="1rem"
                  style={{
                    position: "absolute",
                    bottom: ".2rem",
                    left: ".2rem",
                    zIndex: 10
                  }}
                >
                  <MapboxLogoWhite />
                </Box>

                {/* Mapbox attribution */}
                <Box
                  style={{
                    position: "absolute",
                    bottom: ".2rem",
                    right: ".2rem",
                    zIndex: 10,
                    opacity: 0.7
                  }}
                  pad=".1rem"
                  background="#fff"
                >
                  <Text size=".8rem" color="#000">
                    © Mapbox © OpenStreetMap
                  </Text>
                </Box>
              </Box>

              {/* Legend for boudary */}
              <BoundaryLegend
                screen={screen}
                geographyLevel={geographyLevel}
                showLinks={false}
                showSmallText
              />

              {/* Data sources */}
              <Text size="1.1rem" color="text-700">
                Sources: USDA Forest Service. Wildfire Risk to Communities v2.{" "}
                {new Date().getFullYear()}.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layer>
  );
};

export default MapDownloadDialog;
